import { escapeHtml } from '../lib/escape';
import { compositeLabel, type LabelMode } from '../lib/compositor';
import { DEFAULT_FACE_CROP, getFaceCrop } from '../lib/face-crop';
import {
  clearGenerations,
  generations,
  startUpscale,
  subscribeGenerations,
  type Generation,
} from '../generation';
import { mountBaseImageSection } from './base-image-section';
import { mountAreasSection } from './areas-section';
import { state, subscribeContent } from '../state';

interface ImagesPanelElements {
  baseImageSection: HTMLElement;
  areasSection: HTMLElement;
  grid: HTMLElement;
  lightbox: HTMLElement;
  lightboxStage: HTMLElement;
  lightboxInfo: HTMLElement;
  lightboxClose: HTMLButtonElement;
  lightboxPrev: HTMLButtonElement;
  lightboxNext: HTMLButtonElement;
  lightboxDownload: HTMLButtonElement;
  lightboxPhotoToggle: HTMLButtonElement;
  lightboxBaseToggle: HTMLButtonElement;
  lightboxResize: HTMLElement;
  lightboxPhotoOverlay: HTMLElement;
  lightboxTopbarLeft: HTMLElement;
  lightboxModeButtons: HTMLButtonElement[];
  lightboxModeSelect: HTMLSelectElement;
  lightboxInfoBtn: HTMLButtonElement;
  infoPopover: HTMLElement;
  errorPopover: HTMLElement;
  downloadAllBtn: HTMLButtonElement;
  clearAllBtn: HTMLButtonElement;
  modeSelect: HTMLSelectElement;
}

let lightboxIndex = 0;
const LIGHTBOX_MODE_KEY = 'espolon.lightboxMode';
const LIGHTBOX_SHOW_PHOTO_KEY = 'espolon.lightboxShowPhoto';
const LIGHTBOX_SHOW_BASE_KEY = 'espolon.lightboxShowBase';
const LIGHTBOX_INFO_HEIGHT_KEY = 'espolon.lightboxInfoHeight';
const GRID_MODE_KEY = 'espolon.gridMode';
const VALID_MODES: LabelMode[] = ['illustration', 'label-full', 'label-trimmed'];

function loadStoredMode(key: string, fallback: LabelMode): LabelMode {
  try {
    const v = localStorage.getItem(key);
    if (v && (VALID_MODES as string[]).includes(v)) return v as LabelMode;
  } catch {}
  return fallback;
}

let lightboxMode: LabelMode = loadStoredMode(LIGHTBOX_MODE_KEY, 'label-trimmed');
let lightboxShowPhoto: boolean = (() => {
  try {
    return localStorage.getItem(LIGHTBOX_SHOW_PHOTO_KEY) === '1';
  } catch {
    return false;
  }
})();
let lightboxShowBase: boolean = (() => {
  try {
    return localStorage.getItem(LIGHTBOX_SHOW_BASE_KEY) === '1';
  } catch {
    return false;
  }
})();
let gridMode: LabelMode = loadStoredMode(GRID_MODE_KEY, 'label-trimmed');
let renderSeq = 0;
let gridRenderSeq = 0;
const previewCache = new Map<string, string>();

function previewKey(gen: Generation, mode: LabelMode): string {
  const c = state.cropArea;
  const cropKey = c ? `${c.x}x${c.y}x${c.w}x${c.h}` : 'full';
  return `${gen.id}:${mode}:${cropKey}`;
}

function cropKeyFromState(): string {
  const c = state.cropArea;
  return c ? `${c.x}x${c.y}x${c.w}x${c.h}` : 'full';
}

function invalidateAllPreviewCache(): void {
  for (const url of previewCache.values()) {
    if (url.startsWith('blob:')) URL.revokeObjectURL(url);
  }
  previewCache.clear();
}

function invalidatePreviewCacheFor(genId: string): void {
  const prefix = `${genId}:`;
  for (const key of Array.from(previewCache.keys())) {
    if (key.startsWith(prefix)) {
      const url = previewCache.get(key);
      if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
      previewCache.delete(key);
    }
  }
}

function elapsedSec(g: Generation): number {
  return Math.max(0, (Date.now() - g.startedAt) / 1000);
}

function formatElapsed(g: Generation): string {
  return `${elapsedSec(g).toFixed(1)}s`;
}

let elapsedTicker: number | null = null;
function ensureElapsedTicker(): void {
  if (elapsedTicker != null) return;
  elapsedTicker = window.setInterval(() => {
    const anyRunning = generations.some(
      (g) =>
        g.status === 'queued' ||
        g.status === 'running' ||
        g.upscaleStatus === 'running',
    );
    if (!anyRunning) {
      if (elapsedTicker != null) {
        window.clearInterval(elapsedTicker);
        elapsedTicker = null;
      }
      return;
    }
    for (const g of generations) {
      if (g.status === 'queued' || g.status === 'running') {
        const nodes = document.querySelectorAll<HTMLElement>(
          `[data-gen-elapsed="${CSS.escape(g.id)}"]`,
        );
        const label = formatElapsed(g);
        nodes.forEach((n) => {
          n.textContent = label;
        });
      }
      if (g.upscaleStatus === 'running' && g.upscaleStartedAt != null) {
        const sec = Math.max(0, (Date.now() - g.upscaleStartedAt) / 1000);
        const label = `${sec.toFixed(1)}s`;
        const nodes = document.querySelectorAll<HTMLElement>(
          `[data-gen-upscale-elapsed="${CSS.escape(g.id)}"]`,
        );
        nodes.forEach((n) => {
          n.textContent = label;
        });
      }
    }
  }, 100);
}

async function getPreviewUrl(gen: Generation, mode: LabelMode): Promise<string> {
  if (mode === 'illustration') return gen.dataUrl!;
  const key = previewKey(gen, mode);
  const cached = previewCache.get(key);
  if (cached) return cached;
  const ref = state.baseImage
    ? { w: state.baseImage.width, h: state.baseImage.height }
    : null;
  const blob = await compositeLabel(gen.dataUrl!, mode, state.cropArea, ref);
  const url = URL.createObjectURL(blob);
  previewCache.set(key, url);
  return url;
}

export function mountImagesPanel(el: ImagesPanelElements): void {
  mountBaseImageSection({ container: el.baseImageSection });
  mountAreasSection({ container: el.areasSection });

  el.modeSelect.value = gridMode;
  const render = (): void => renderGrid(el.grid);
  subscribeGenerations(render);
  render();

  let lastCropKey = cropKeyFromState();
  subscribeContent(() => {
    const nextKey = cropKeyFromState();
    if (nextKey === lastCropKey) return;
    lastCropKey = nextKey;
    invalidateAllPreviewCache();
    render();
    if (!el.lightbox.classList.contains('hidden')) renderLightbox(el);
  });

  el.modeSelect.addEventListener('change', () => {
    const v = el.modeSelect.value as LabelMode;
    if (!VALID_MODES.includes(v) || v === gridMode) return;
    gridMode = v;
    try {
      localStorage.setItem(GRID_MODE_KEY, v);
    } catch {}
    render();
  });

  el.grid.addEventListener('click', (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const actionEl = target.closest<HTMLElement>('[data-gen-action]');
    const tileEl = target.closest<HTMLElement>('[data-gen-id]');
    if (!tileEl) return;
    const id = tileEl.dataset.genId!;
    const idx = generations.findIndex((g) => g.id === id);
    if (idx === -1) return;
    const gen = generations[idx];

    if (!actionEl) {
      if (gen.status === 'done') openLightbox(el, idx);
      return;
    }

    e.stopPropagation();
    const action = actionEl.dataset.genAction;
    switch (action) {
      case 'info':
        showInfoPopover(el.infoPopover, actionEl, gen);
        break;
      case 'error':
        showErrorPopover(el.errorPopover, actionEl, gen);
        break;
      case 'download':
        downloadGeneration(gen);
        break;
      case 'upscale':
        void startUpscale(gen, (g) => invalidatePreviewCacheFor(g.id));
        ensureElapsedTicker();
        break;
    }
  });

  document.addEventListener('click', (e) => {
    const target = e.target;
    if (!(target instanceof Node)) return;
    if (!el.infoPopover.classList.contains('hidden') && !el.infoPopover.contains(target)) {
      el.infoPopover.classList.add('hidden');
    }
    if (!el.errorPopover.classList.contains('hidden') && !el.errorPopover.contains(target)) {
      el.errorPopover.classList.add('hidden');
    }
  });

  el.downloadAllBtn.addEventListener('click', () => {
    void downloadAllGenerations();
  });
  el.clearAllBtn.addEventListener('click', () => {
    const done = generations.filter((g) => g.status === 'done').length;
    const msg =
      generations.length === 0
        ? null
        : `Clear ${generations.length} generation${generations.length === 1 ? '' : 's'}${done ? ` (${done} completed)` : ''}?`;
    if (msg && !window.confirm(msg)) return;
    clearGenerations();
    if (!el.lightbox.classList.contains('hidden')) closeLightbox(el);
  });

  el.lightboxClose.addEventListener('click', () => closeLightbox(el));
  el.lightboxPrev.addEventListener('click', () => stepLightbox(el, -1));
  el.lightboxNext.addEventListener('click', () => stepLightbox(el, 1));
  el.lightboxDownload.addEventListener('click', () => {
    const gen = generations[lightboxIndex];
    if (gen) void downloadGeneration(gen, lightboxMode);
  });
  el.lightboxModeButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const mode = btn.dataset.mode as LabelMode;
      if (!mode || mode === lightboxMode) return;
      lightboxMode = mode;
      try {
        localStorage.setItem(LIGHTBOX_MODE_KEY, mode);
      } catch {}
      el.lightboxModeButtons.forEach((b) =>
        b.classList.toggle('is-active', b.dataset.mode === mode),
      );
      renderLightbox(el);
    });
  });

  el.lightboxModeSelect.value = lightboxMode;
  el.lightboxModeSelect.addEventListener('change', () => {
    const mode = el.lightboxModeSelect.value as LabelMode;
    if (!VALID_MODES.includes(mode) || mode === lightboxMode) return;
    lightboxMode = mode;
    try { localStorage.setItem(LIGHTBOX_MODE_KEY, mode); } catch {}
    el.lightboxModeButtons.forEach((b) =>
      b.classList.toggle('is-active', b.dataset.mode === mode),
    );
    renderLightbox(el);
  });

  const promptModal = document.createElement('div');
  promptModal.className = 'lightbox-prompt-modal hidden';
  document.body.appendChild(promptModal);

  const openPromptModal = (): void => {
    const gen = generations[lightboxIndex];
    if (!gen) return;
    promptModal.innerHTML = `
      <div class="lightbox-prompt-modal-header">
        <span class="lightbox-prompt-modal-title">Assembled Prompt</span>
        <button type="button" class="lightbox-topbar-btn lightbox-prompt-modal-close" data-close-prompt-modal aria-label="Close">×</button>
      </div>
      <div class="lightbox-prompt-modal-body">
        <button type="button" class="lightbox-copy-icon" data-prompt-modal-copy aria-label="Copy prompt" title="Copy prompt">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
        </button>
        <pre class="lightbox-prompt">${assembledPromptHtml(gen)}</pre>
      </div>
    `;
    promptModal.classList.remove('hidden');
  };
  const closePromptModal = (): void => promptModal.classList.add('hidden');

  el.lightboxInfoBtn.addEventListener('click', openPromptModal);

  promptModal.addEventListener('click', async (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (t.closest('[data-close-prompt-modal]')) { closePromptModal(); return; }
    const copyBtn = t.closest<HTMLButtonElement>('[data-prompt-modal-copy]');
    if (!copyBtn) return;
    const gen = generations[lightboxIndex];
    if (!gen) return;
    try {
      await navigator.clipboard.writeText(gen.prompt);
      copyBtn.classList.add('copied');
      setTimeout(() => copyBtn.classList.remove('copied'), 1600);
    } catch {
      copyBtn.classList.add('copy-failed');
      setTimeout(() => copyBtn.classList.remove('copy-failed'), 1600);
    }
  });

  el.lightboxPhotoToggle.classList.toggle('is-active', lightboxShowPhoto);
  el.lightboxPhotoToggle.addEventListener('click', () => {
    lightboxShowPhoto = !lightboxShowPhoto;
    try {
      localStorage.setItem(LIGHTBOX_SHOW_PHOTO_KEY, lightboxShowPhoto ? '1' : '0');
    } catch {}
    el.lightboxPhotoToggle.classList.toggle('is-active', lightboxShowPhoto);
    renderLightbox(el);
  });

  el.lightboxBaseToggle.classList.toggle('is-active', lightboxShowBase);
  el.lightboxBaseToggle.addEventListener('click', () => {
    lightboxShowBase = !lightboxShowBase;
    try {
      localStorage.setItem(LIGHTBOX_SHOW_BASE_KEY, lightboxShowBase ? '1' : '0');
    } catch {}
    el.lightboxBaseToggle.classList.toggle('is-active', lightboxShowBase);
    renderLightbox(el);
  });

  const storedInfoHeight = (() => {
    try {
      const v = Number(localStorage.getItem(LIGHTBOX_INFO_HEIGHT_KEY));
      return Number.isFinite(v) && v > 0 ? v : 0;
    } catch {
      return 0;
    }
  })();
  if (storedInfoHeight) el.lightboxInfo.style.height = `${storedInfoHeight}px`;
  mountLightboxInfoResize(el.lightboxResize, el.lightboxInfo);

  el.lightbox.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    if (
      t.closest(
        '.lightbox-topbar, .lightbox-nav, .lightbox-info, .lightbox-stage img, .lightbox-message',
      )
    )
      return;
    closeLightbox(el);
  });

  el.lightboxInfo.addEventListener('click', async (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const copyBtn = target.closest<HTMLButtonElement>('[data-lightbox-copy]');
    if (!copyBtn) return;
    e.stopPropagation();
    const gen = generations[lightboxIndex];
    if (!gen) return;
    try {
      await navigator.clipboard.writeText(gen.prompt);
      copyBtn.classList.add('copied');
      setTimeout(() => copyBtn.classList.remove('copied'), 1600);
    } catch {
      copyBtn.classList.add('copy-failed');
      setTimeout(() => copyBtn.classList.remove('copy-failed'), 1600);
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (!el.infoPopover.classList.contains('hidden')) {
        el.infoPopover.classList.add('hidden');
        return;
      }
      if (!el.errorPopover.classList.contains('hidden')) {
        el.errorPopover.classList.add('hidden');
        return;
      }
      if (!promptModal.classList.contains('hidden')) {
        closePromptModal();
        return;
      }
      if (!el.lightbox.classList.contains('hidden')) {
        closeLightbox(el);
        return;
      }
    }
    if (el.lightbox.classList.contains('hidden')) return;
    if (e.key === 'ArrowLeft') stepLightbox(el, -1);
    if (e.key === 'ArrowRight') stepLightbox(el, 1);
  });

  subscribeGenerations(() => {
    if (!el.lightbox.classList.contains('hidden')) renderLightbox(el);
  });
}

function renderGrid(grid: HTMLElement): void {
  if (!generations.length) {
    grid.innerHTML = `<div class="images-empty">No generations yet. Press Generate to start.</div>`;
    return;
  }
  grid.dataset.mode = gridMode;
  grid.innerHTML = generations.map((g) => renderTile(g)).join('');
  ensureElapsedTicker();
  applyFacePositions(grid);
  if (gridMode === 'illustration') return;
  const token = ++gridRenderSeq;
  for (const g of generations) {
    if (g.status !== 'done' || !g.dataUrl) continue;
    void getPreviewUrl(g, gridMode).then((url) => {
      if (token !== gridRenderSeq) return;
      const img = grid.querySelector<HTMLImageElement>(
        `[data-gen-id="${g.id}"] .gen-thumb img`,
      );
      if (img) img.src = url;
    });
  }
}

function applyFacePositions(grid: HTMLElement): void {
  for (const g of generations) {
    const photo = g.photoInputs[0];
    if (!photo) continue;
    void getFaceCrop(photo.displayDataUrl).then((crop) => {
      const el = grid.querySelector<HTMLElement>(`[data-gen-face="${CSS.escape(g.id)}"]`);
      if (!el) return;
      el.style.backgroundPosition = crop.position;
      el.style.backgroundSize = crop.size;
    });
  }
}

function faceAvatarHtml(g: Generation): string {
  const photo = g.photoInputs[0];
  if (!photo) return '';
  return `<div class="gen-face" data-gen-face="${escapeHtml(g.id)}" style="background-image:url('${photo.displayDataUrl}');background-position:${DEFAULT_FACE_CROP.position};background-size:${DEFAULT_FACE_CROP.size};" title="${escapeHtml(photo.questionTitle)}"></div>`;
}

function renderTile(g: Generation): string {
  const durationLabel =
    g.durationMs != null ? `${(g.durationMs / 1000).toFixed(1)}s` : '';
  const showSpinner = g.status === 'queued' || g.status === 'running';
  const hasImage = (g.status === 'done' || g.status === 'running') && !!g.dataUrl;
  const hasError = g.status === 'error' || g.errorLog.length > 0;
  const progressLabel =
    g.status === 'running' && g.areaCount > 0
      ? `${Math.min(g.areaIndex + 1, g.areaCount)}/${g.areaCount}${
          g.attempts > 1 ? ` · retry ${g.attempts - 1}` : ''
        }`
      : '';
  const retryLabel = progressLabel;
  const elapsedLiveLabel = showSpinner ? formatElapsed(g) : '';

  const selectionChips = g.selections
    .map(
      (s) =>
        `<span class="gen-selection-chip c${s.colorIndex}" title="${escapeHtml(s.promptLabel)}: ${escapeHtml(s.label)}">${escapeHtml(s.label)}</span>`,
    )
    .join('');

  const tileSrc = hasImage
    ? gridMode === 'illustration'
      ? g.dataUrl!
      : (previewCache.get(previewKey(g, gridMode)) ?? g.dataUrl!)
    : '';

  return `
    <div class="gen-tile" data-gen-id="${g.id}" data-status="${g.status}">
      <div class="gen-thumb">
        ${hasImage ? `<img src="${tileSrc}" alt="Generated image" />` : ''}
        ${showSpinner ? `<div class="gen-progress" style="--gen-progress-delay:-${elapsedSec(g).toFixed(2)}s"></div>` : ''}
        ${g.status === 'error' && !hasImage ? `<div class="gen-error-bg">!</div>` : ''}
        ${faceAvatarHtml(g)}
        ${selectionChips ? `<div class="gen-chips gen-chips-br">${selectionChips}</div>` : ''}
        <div class="gen-chips gen-chips-tr">
          ${upscaleTimeChipHtml(g)}
          ${g.upscaleStatus === 'done' ? `<span class="gen-chip upscale">4x</span>` : ''}
          ${elapsedLiveLabel ? `<span class="gen-chip time" data-gen-elapsed="${escapeHtml(g.id)}">${elapsedLiveLabel}</span>` : ''}
          ${durationLabel ? `<span class="gen-chip time">${durationLabel}</span>` : ''}
          ${retryLabel ? `<span class="gen-chip retry">${retryLabel}</span>` : ''}
        </div>
        <div class="gen-actions">
          <button type="button" class="gen-action" data-gen-action="info" title="View info" aria-label="Info">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          </button>
          ${
            hasError
              ? `<button type="button" class="gen-action error" data-gen-action="error" title="View errors" aria-label="Errors">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                </button>`
              : ''
          }
          ${
            hasImage
              ? `<button type="button" class="gen-action" data-gen-action="download" title="Download" aria-label="Download">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                </button>`
              : ''
          }
          ${upscaleActionHtml(g, hasImage)}
        </div>
      </div>
    </div>
  `;
}

function upscaleTimeChipHtml(g: Generation): string {
  if (g.upscaleStatus === 'running' && g.upscaleStartedAt != null) {
    const sec = Math.max(0, (Date.now() - g.upscaleStartedAt) / 1000);
    return `<span class="gen-chip upscale-time" data-gen-upscale-elapsed="${escapeHtml(g.id)}">${sec.toFixed(1)}s</span>`;
  }
  if (g.upscaleStatus === 'done' && g.upscaleDurationMs != null) {
    return `<span class="gen-chip upscale-time">${(g.upscaleDurationMs / 1000).toFixed(1)}s</span>`;
  }
  return '';
}

function upscaleActionHtml(g: Generation, hasImage: unknown): string {
  if (!hasImage) return '';
  if (g.upscaleStatus === 'done') return '';
  if (g.upscaleStatus === 'running') {
    return `<button type="button" class="gen-action upscaling" disabled title="Upscaling…" aria-label="Upscaling">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>
    </button>`;
  }
  const errTitle = g.upscaleStatus === 'error'
    ? `Upscale failed: ${g.upscaleError ?? 'unknown error'} — click to retry`
    : 'Upscale 4x';
  return `<button type="button" class="gen-action${g.upscaleStatus === 'error' ? ' error' : ''}" data-gen-action="upscale" title="${escapeHtml(errTitle)}" aria-label="Upscale">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="17 11 12 6 7 11"/><polyline points="17 18 12 13 7 18"/></svg>
  </button>`;
}

function assembledPromptHtml(gen: Generation): string {
  return escapeHtml(gen.prompt);
}

function selectionChipsHtml(gen: Generation): string {
  return gen.selections
    .map(
      (s) =>
        `<span class="gen-selection-chip c${s.colorIndex}" title="${escapeHtml(s.promptLabel)}: ${escapeHtml(s.label)}">${escapeHtml(s.label)}</span>`,
    )
    .join('');
}

function metaChipsHtml(gen: Generation): string {
  const durationLabel =
    gen.durationMs != null ? `${(gen.durationMs / 1000).toFixed(1)}s` : '';
  return durationLabel ? `<span class="gen-chip time">${durationLabel}</span>` : '';
}

function showInfoPopover(popover: HTMLElement, anchor: HTMLElement, gen: Generation): void {
  const chips = selectionChipsHtml(gen);
  popover.innerHTML = `
    <div class="info-section">
      <div class="info-label">Selections</div>
      <div class="info-chips">${chips || '<span class="info-muted">None</span>'}</div>
    </div>
    <div class="info-section">
      <div class="lightbox-prompt-header">
        <span class="info-label">Assembled Prompt</span>
        <button type="button" class="lightbox-copy" data-info-copy>Copy</button>
      </div>
      <pre class="info-prompt">${assembledPromptHtml(gen)}</pre>
    </div>
  `;
  positionPopover(popover, anchor);
  popover.classList.remove('hidden');

  const copyBtn = popover.querySelector<HTMLButtonElement>('[data-info-copy]');
  copyBtn?.addEventListener('click', async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(gen.prompt);
      copyBtn.textContent = 'Copied!';
      copyBtn.classList.add('copied');
      setTimeout(() => {
        copyBtn.textContent = 'Copy';
        copyBtn.classList.remove('copied');
      }, 1600);
    } catch {
      copyBtn.textContent = 'Copy failed';
      setTimeout(() => (copyBtn.textContent = 'Copy'), 1600);
    }
  });
}

function showErrorPopover(popover: HTMLElement, anchor: HTMLElement, gen: Generation): void {
  const items = gen.errorLog.map((e) => `<li>${escapeHtml(e)}</li>`).join('');
  popover.innerHTML = `
    <div class="info-label">Errors</div>
    <ul class="error-list">${items}</ul>
  `;
  positionPopover(popover, anchor);
  popover.classList.remove('hidden');
}

function positionPopover(popover: HTMLElement, anchor: HTMLElement): void {
  const rect = anchor.getBoundingClientRect();
  popover.style.top = `${rect.bottom + 6}px`;
  popover.style.right = `${Math.max(8, window.innerWidth - rect.right)}px`;
}

async function downloadAllGenerations(): Promise<void> {
  const done = generations.filter((g) => g.status === 'done' && g.dataUrl);
  for (const gen of done) {
    downloadGeneration(gen);
    await new Promise((resolve) => setTimeout(resolve, 120));
  }
}

async function downloadGeneration(
  gen: Generation,
  mode: LabelMode = 'illustration',
): Promise<void> {
  if (!gen.dataUrl) return;
  const id = gen.id.slice(0, 8);
  if (mode === 'illustration') {
    const a = document.createElement('a');
    a.href = gen.dataUrl;
    const rawExt = (gen.mimeType ?? 'image/png').split('/')[1] || 'png';
    const ext = rawExt === 'jpeg' ? 'jpg' : rawExt;
    a.download = `espolon-${id}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return;
  }
  const url = await getPreviewUrl(gen, mode);
  const a = document.createElement('a');
  a.href = url;
  const suffix = mode === 'label-full' ? 'label-full' : 'label-trimmed';
  a.download = `espolon-${id}-${suffix}.png`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function openLightbox(el: ImagesPanelElements, idx: number): void {
  lightboxIndex = idx;
  el.lightboxModeButtons.forEach((b) =>
    b.classList.toggle('is-active', b.dataset.mode === lightboxMode),
  );
  el.lightboxModeSelect.value = lightboxMode;
  el.lightbox.classList.remove('hidden');
  renderLightbox(el);
}

function closeLightbox(el: ImagesPanelElements): void {
  el.lightbox.classList.add('hidden');
  el.lightboxStage.innerHTML = '';
  el.lightboxInfo.innerHTML = '';
  el.lightboxTopbarLeft.innerHTML = '';
  el.lightboxPhotoOverlay.innerHTML = '';
  el.lightboxPhotoOverlay.classList.add('hidden');
  document.querySelector('.lightbox-prompt-modal')?.classList.add('hidden');
}

function stepLightbox(el: ImagesPanelElements, delta: number): void {
  if (!generations.length) return;
  lightboxIndex = (lightboxIndex + delta + generations.length) % generations.length;
  renderLightbox(el);
}

function renderLightbox(el: ImagesPanelElements): void {
  const gen = generations[lightboxIndex];
  if (!gen) {
    closeLightbox(el);
    return;
  }
  el.lightboxStage.classList.toggle('is-checker', lightboxMode === 'label-trimmed');
  const photoInput = gen.photoInputs[0];
  const showPhotoPane = lightboxShowPhoto && !!photoInput;
  if (showPhotoPane) {
    el.lightboxPhotoOverlay.innerHTML = `<img src="${photoInput!.displayDataUrl}" alt="Photo input"/>`;
    el.lightboxPhotoOverlay.classList.remove('hidden');
  } else {
    el.lightboxPhotoOverlay.innerHTML = '';
    el.lightboxPhotoOverlay.classList.add('hidden');
  }
  const setMain = (inner: string): void => {
    el.lightboxStage.innerHTML = inner;
  };
  const canShowBase = lightboxMode === 'illustration' && !!state.baseImage?.dataUrl;
  el.lightboxBaseToggle.classList.toggle('hidden', !canShowBase);
  el.lightboxBaseToggle.classList.toggle('is-active', canShowBase && lightboxShowBase);
  if (gen.status === 'done' && gen.dataUrl) {
    if (lightboxMode === 'illustration') {
      const src = canShowBase && lightboxShowBase ? state.baseImage!.dataUrl : gen.dataUrl;
      setMain(`<img src="${src}" alt="Generated image"/>`);
    } else {
      const renderToken = ++renderSeq;
      setMain(`<div class="gen-spinner large"></div>`);
      void getPreviewUrl(gen, lightboxMode)
        .then((url) => {
          if (renderToken !== renderSeq) return;
          setMain(`<img src="${url}" alt="Label preview"/>`);
        })
        .catch((err: unknown) => {
          if (renderToken !== renderSeq) return;
          setMain(`<div class="lightbox-message">Preview failed: ${escapeHtml(String(err))}</div>`);
        });
    }
  } else if (gen.status === 'error') {
    setMain(`<div class="lightbox-message">Error: ${escapeHtml(gen.finalError ?? 'Generation failed')}</div>`);
  } else {
    setMain(
      `<div class="gen-progress" style="--gen-progress-delay:-${elapsedSec(gen).toFixed(2)}s"></div><div class="gen-spinner large"><span class="gen-elapsed" data-gen-elapsed="${escapeHtml(gen.id)}">${formatElapsed(gen)}</span></div>`,
    );
    ensureElapsedTicker();
  }
  el.lightboxDownload.disabled = gen.status !== 'done' || !gen.dataUrl;

  const chips = selectionChipsHtml(gen);
  const photo = gen.photoInputs[0];
  const faceHtml = photo
    ? `<div class="lightbox-info-face" data-lightbox-face style="background-image:url('${photo.displayDataUrl}');background-position:${DEFAULT_FACE_CROP.position};background-size:${DEFAULT_FACE_CROP.size};" title="${escapeHtml(photo.questionTitle)}"></div>`
    : '';
  el.lightboxTopbarLeft.innerHTML = `
    ${faceHtml}
    <div class="lightbox-info-chips">${chips || '<span class="info-muted">No selections</span>'}</div>
    <div class="lightbox-info-meta">${metaChipsHtml(gen)}</div>
  `;
  el.lightboxInfo.innerHTML = `
    <button type="button" class="lightbox-copy-icon" data-lightbox-copy aria-label="Copy prompt" title="Copy prompt">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
    </button>
    <div class="lightbox-info-scroll">
      <pre class="lightbox-prompt">${assembledPromptHtml(gen)}</pre>
    </div>
  `;
  if (photo) {
    void getFaceCrop(photo.displayDataUrl).then((crop) => {
      const faceEl = el.lightboxTopbarLeft.querySelector<HTMLElement>('[data-lightbox-face]');
      if (!faceEl) return;
      faceEl.style.backgroundPosition = crop.position;
      faceEl.style.backgroundSize = crop.size;
    });
  }
}

function mountLightboxInfoResize(handle: HTMLElement, info: HTMLElement): void {
  let dragging = false;
  let startY = 0;
  let startHeight = 0;
  handle.addEventListener('mousedown', (e) => {
    dragging = true;
    startY = e.clientY;
    startHeight = info.getBoundingClientRect().height;
    handle.classList.add('dragging');
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  });
  document.addEventListener('mousemove', (e) => {
    if (!dragging) return;
    const delta = startY - e.clientY;
    const max = Math.max(120, window.innerHeight - 200);
    const height = Math.max(80, Math.min(startHeight + delta, max));
    info.style.height = `${height}px`;
  });
  document.addEventListener('mouseup', () => {
    if (!dragging) return;
    dragging = false;
    handle.classList.remove('dragging');
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    try {
      localStorage.setItem(
        LIGHTBOX_INFO_HEIGHT_KEY,
        String(Math.round(info.getBoundingClientRect().height)),
      );
    } catch {}
    // Suppress the click that fires after mouseup so it doesn't close the lightbox
    document.addEventListener('click', (e) => e.stopPropagation(), { capture: true, once: true });
  });
}
